import React, { useCallback, useState } from 'react';
import { Alert, Button, Table, Tag, Typography } from 'antd';

import { useAccess } from '../../../auth/AccessContext.jsx';
import ActionIcon from '../../../components/ActionIcon.jsx';
import { formatMoney, formatUnits } from '../../../format';
import RegisterFilters, { rangeDates } from './RegisterFilters.jsx';
import { LEGACY_TYPE_COLOUR, LEGACY_TYPE_LABEL, LEGACY_TYPE_OPTIONS, historyQuery } from './register.js';
import { usePagedList } from './usePagedList.js';

const { Text } = Typography;

const dash = (value, format = (v) => v) => (value === null || value === undefined || value === '' ? '—' : format(value));

/**
 * The INITIAL history as GET register/history answers it (legacy_transaction,
 * IP-109): the transactions an INITIAL TRANSACTIONS file loaded, in LIV's own
 * words (TYPE S, R, SS, SR and its TRANSACTION_ID and REF_ID). They are
 * outside the register's fold - no balance moved for them - so they are a
 * table of their own, never mixed with the register's transactions. Units are
 * as the file gave them, unsigned. Rendered by LegacyHistory, and on its own
 * by UnitRegistry.test.js.
 */
export const HistoryTable = ({ items, loading = false }) => {
    const columns = [
        { title: 'Effective date', dataIndex: 'dealingDate', key: 'dealingDate', width: 110 },
        { title: 'Fund', dataIndex: 'fundId', key: 'fundId', width: 80 },
        {
            title: 'Holder',
            key: 'holder',
            render: (_, h) => (
                <div className="history-holder">
                    <div>{dash(h.fullName)}</div>
                    <Text type="secondary" className="history-id-chip">{dash(h.holderNo)}</Text>
                </div>
            )
        },
        {
            title: 'Type',
            dataIndex: 'txnType',
            key: 'txnType',
            render: (v) => <Tag color={LEGACY_TYPE_COLOUR[v] ?? 'default'} className="history-type-tag">{(LEGACY_TYPE_LABEL[v] ?? v).toUpperCase()}</Tag>
        },
        { title: 'Transaction ID', dataIndex: 'externalRef', key: 'externalRef', render: (v) => <Text className="history-id-chip">{dash(v)}</Text> },
        { title: 'Ref ID', dataIndex: 'refId', key: 'refId', render: (v) => dash(v) },
        { title: 'Units', dataIndex: 'units', key: 'units', align: 'right', render: (v) => formatUnits(v) },
        { title: 'Price', dataIndex: 'price', key: 'price', align: 'right', render: (v) => dash(v, formatUnits) },
        { title: 'Amount (LKR)', dataIndex: 'amount', key: 'amount', align: 'right', render: (v) => dash(v, formatMoney) },
        { title: 'Fee (LKR)', dataIndex: 'fee', key: 'fee', align: 'right', render: (v) => dash(v, formatMoney) },
        { title: 'File row', dataIndex: 'rowNo', key: 'rowNo', align: 'right', width: 90 }
    ];
    return (
        <Table rowKey="legacyTxnId" className="register-table register-history-table" size="small" loading={loading} columns={columns} dataSource={items} pagination={false} scroll={{ x: 1100 }}
            locale={{ emptyText: 'No history loaded' }} />
    );
};

/**
 * The INITIAL history, filtered by fund, holder, LIV type and dealing date, a
 * page at a time with Load more (register.js historyQuery).
 */
const LegacyHistory = ({ funds, fundId, onFund, holder, onHolder }) => {
    const { api } = useAccess();
    const [txnType, setTxnType] = useState(null);
    const [range, setRange] = useState(null);
    const { from, to } = rangeDates(range);
    const holderId = holder?.holderId ?? null;

    const fetchPage = useCallback(
        (cursor) => api.register.history(historyQuery({ fundId, holderId, txnType, from, to, cursor })),
        [api, fundId, holderId, txnType, from, to]
    );
    const list = usePagedList(fetchPage, 'legacyTxnId');

    return (
        <>
            <div className="register-info register-holders-info">
                <ActionIcon name="info" />
                <span>
                    The transaction history an initial load brought in from LIV, before the Core dealt. It is kept as it was loaded and
                    moves no balance: the register's own transactions are on the Transactions tab.
                </span>
            </div>
            <RegisterFilters
                funds={funds} fundId={fundId} onFund={onFund} holder={holder} onHolder={onHolder}
                txnType={txnType} typeOptions={LEGACY_TYPE_OPTIONS} onType={setTxnType}
                range={range} onRange={setRange}
                onClear={() => { setTxnType(null); setRange(null); onHolder(null); }}
            />
            {list.error && <Alert type="error" showIcon message={list.error} className="auth-alert" closable onClose={list.clearError} />}
            <HistoryTable items={list.items} loading={list.loading} />
            <div className="register-list-footer">
                <Text type="secondary">{list.items.length} shown{list.nextCursor ? ', more to load' : ''}</Text>
                {list.nextCursor && <Button onClick={list.loadMore} loading={list.loadingMore}>Load more</Button>}
            </div>
        </>
    );
};

export default LegacyHistory;
